/**
 * The mission timeline's scale (Sprint 3, S3-15 / D14): a fixed playhead in the middle of the track, the track
 * scrolls under it. Zoom is the visible span, from 70 years down to one day, in steps of a factor ZOOM_STEP. Pure:
 * time ↔ px, tick generation (decades → years → months → days) and magnetic snap.
 */

export const DAY = 86_400_000;
export const YEAR = 365.25 * DAY;
/** Visible span limits (D14). */
export const MAX_SPAN = 70 * YEAR;
export const MIN_SPAN = DAY;
/** One zoom unit (keyboard PageUp/PageDown, +/−, the zoom buttons). */
export const ZOOM_STEP = 2;

export interface TimelineView {
  /** Time under the playhead (the middle of the track), UTC ms. */
  center: number;
  /** Visible span of the track, ms. */
  span: number;
  /** Track width, px. */
  width: number;
}

export function clampSpan(span: number): number {
  return Math.min(MAX_SPAN, Math.max(MIN_SPAN, span));
}

/** Milliseconds per pixel. */
export function msPerPx(v: TimelineView): number {
  return v.span / Math.max(v.width, 1);
}

/** Pixel position of time `t` on the track (0 = left edge). */
export function timeToX(v: TimelineView, t: number): number {
  return v.width / 2 + (t - v.center) / msPerPx(v);
}

/** Time at pixel `x`. Inverse of timeToX. */
export function xToTime(v: TimelineView, x: number): number {
  return v.center + (x - v.width / 2) * msPerPx(v);
}

/** The span after `steps` zoom units (positive = zoom in), clamped. */
export function zoomSpan(span: number, steps: number): number {
  return clampSpan(span / Math.pow(ZOOM_STEP, steps));
}

export type TickUnit = 'decade' | 'year' | 'month' | 'day' | 'hour';

export interface Tick {
  time: number;
  unit: TickUnit;
  /** Major ticks carry a label (e.g. every decade, every January, every 1st). */
  major: boolean;
}

/** Candidate tick spacings, coarsest first: [unit of the tick, step count of that unit]. */
const SPACINGS: readonly (readonly [TickUnit, number])[] = [
  ['decade', 1],
  ['year', 5],
  ['year', 1],
  ['month', 3],
  ['month', 1],
  ['day', 7],
  ['day', 1],
  ['hour', 6],
];

/** Approximate length of one unit, ms (only for choosing a spacing). */
const UNIT_MS: Record<TickUnit, number> = {
  decade: 10 * YEAR,
  year: YEAR,
  month: YEAR / 12,
  day: DAY,
  hour: DAY / 24,
};

/** The finest spacing whose ticks are at least `minPx` apart. */
export function tickSpacing(v: TimelineView, minPx = 14): readonly [TickUnit, number] {
  const perPx = msPerPx(v);
  let best = SPACINGS[0]!;
  for (const s of SPACINGS) {
    if ((UNIT_MS[s[0]] * s[1]) / perPx >= minPx) best = s;
  }
  return best;
}

/** First tick of `unit`/`step` at or after `t` (UTC calendar). */
function firstTick(t: number, unit: TickUnit, step: number): Date {
  const d = new Date(t);
  switch (unit) {
    case 'decade': {
      const y = Math.ceil(d.getUTCFullYear() / 10) * 10;
      return new Date(Date.UTC(y, 0, 1));
    }
    case 'year': {
      const y = Math.ceil(d.getUTCFullYear() / step) * step;
      const c = new Date(Date.UTC(y, 0, 1));
      return c.getTime() < t ? new Date(Date.UTC(y + step, 0, 1)) : c;
    }
    case 'month': {
      const m = Math.ceil(d.getUTCMonth() / step) * step;
      const c = new Date(Date.UTC(d.getUTCFullYear(), m, 1));
      return c.getTime() < t ? new Date(Date.UTC(d.getUTCFullYear(), m + step, 1)) : c;
    }
    case 'day': {
      const c = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      return c.getTime() < t ? new Date(c.getTime() + DAY) : c;
    }
    case 'hour': {
      const h = Math.ceil(d.getUTCHours() / step) * step;
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), h));
    }
  }
}

function advance(d: Date, unit: TickUnit, step: number): Date {
  switch (unit) {
    case 'decade':
      return new Date(Date.UTC(d.getUTCFullYear() + 10 * step, 0, 1));
    case 'year':
      return new Date(Date.UTC(d.getUTCFullYear() + step, 0, 1));
    case 'month':
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + step, 1));
    case 'day':
      return new Date(d.getTime() + step * DAY);
    case 'hour':
      return new Date(d.getTime() + step * 3_600_000);
  }
}

/** Whether a tick of this spacing is major (labelled). */
function isMajor(d: Date, unit: TickUnit, step: number): boolean {
  switch (unit) {
    case 'decade':
      return d.getUTCFullYear() % 50 === 0 || step === 1;
    case 'year':
      return step >= 5 ? d.getUTCFullYear() % 10 === 0 : d.getUTCFullYear() % 5 === 0;
    case 'month':
      return d.getUTCMonth() === 0;
    case 'day':
      return step >= 7 ? d.getUTCDate() <= 7 : d.getUTCDate() === 1;
    case 'hour':
      return d.getUTCHours() === 0;
  }
}

/** Ticks inside the visible track (plus a small margin), at least `minPx` apart. */
export function ticks(v: TimelineView, minPx = 14): Tick[] {
  const [unit, step] = tickSpacing(v, minPx);
  const from = xToTime(v, -minPx);
  const to = xToTime(v, v.width + minPx);
  const out: Tick[] = [];
  for (let d = firstTick(from, unit, step); d.getTime() <= to && out.length < 1000; d = advance(d, unit, step)) {
    out.push({ time: d.getTime(), unit, major: isMajor(d, unit, step) });
  }
  return out;
}

/**
 * Magnetic snap (D14): the event time nearest to `t` if it lies within `radiusPx` on the track, else `t`.
 */
export function snap(v: TimelineView, t: number, events: readonly number[], radiusPx = 8): number {
  const limit = radiusPx * msPerPx(v);
  let best = t;
  let bestD = limit;
  for (const e of events) {
    const d = Math.abs(e - t);
    if (d <= bestD) {
      best = e;
      bestD = d;
    }
  }
  return best;
}

/**
 * Inertia after a drag (eased with the given time constant): the velocity (ms of mission time per real s) decays
 * exponentially; returns the new velocity after `dt` seconds. Below `stop` it is zero.
 */
export function decayVelocity(velocity: number, dt: number, tau = 0.35, stop = 1): number {
  const v = velocity * Math.exp(-dt / tau);
  return Math.abs(v) < stop ? 0 : v;
}
